import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import type { AutoOrderSetting } from '@smartphone-tycoon/shared-core';
import { GAME_CONSTANTS } from '../../config/gameConstants';

interface ComponentInfo {
  id: string;
  name: string;
  cost: number;
  manufacturer: string;
  category: string;
}

interface ComponentOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOrder: (
    componentId: string,
    quantity: number,
    autoOrderSettings: Omit<AutoOrderSetting, 'componentId'>
  ) => void;
  component: ComponentInfo | null;
  cash: number;
  existingAutoOrderSetting?: AutoOrderSetting;
}

type Frequency = 'month' | 'quarter' | 'year';

// Auto-order threshold options
const AUTO_ORDER_THRESHOLDS = [
  { value: 'disabled', label: 'Disabled', threshold: null },
  { value: '0', label: '0 units', threshold: 0 },
  { value: '1000', label: '1,000 units', threshold: 1000 },
  { value: '2500', label: '2,500 units', threshold: 2500 },
];

export function ComponentOrderModal({
  isOpen,
  onClose,
  onOrder,
  component,
  cash,
  existingAutoOrderSetting,
}: ComponentOrderModalProps) {
  const [quantity, setQuantity] = useState<number>(0);
  const [autoOrderThreshold, setAutoOrderThreshold] = useState<string>('disabled');
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [frequency, setFrequency] = useState<Frequency>('month');

  // Reset form when modal opens or component changes
  useEffect(() => {
    if (isOpen && component) {
      if (existingAutoOrderSetting) {
        setQuantity(existingAutoOrderSetting.quantity);
        // Handle both new and legacy settings
        if (existingAutoOrderSetting.autoOrderThreshold !== undefined) {
          const thresholdOption = AUTO_ORDER_THRESHOLDS.find(
            opt => opt.threshold === existingAutoOrderSetting.autoOrderThreshold
          );
          setAutoOrderThreshold(thresholdOption?.value ?? 'disabled');
        } else if (existingAutoOrderSetting.orderOnZeroInventory) {
          // Legacy migration
          setAutoOrderThreshold('0');
        } else {
          setAutoOrderThreshold('disabled');
        }
        setRecurringEnabled(existingAutoOrderSetting.recurringOrder.enabled);
        setFrequency(existingAutoOrderSetting.recurringOrder.frequency);
      } else {
        setQuantity(1000);
        setAutoOrderThreshold('disabled');
        setRecurringEnabled(false);
        setFrequency('month');
      }
    }
  }, [isOpen, component, existingAutoOrderSetting]);

  const totalCost = (component?.cost || 0) * quantity;
  const canAfford = cash >= totalCost;
  const canOrder = quantity > 0 && canAfford;

  // Calculate max affordable quantity
  const maxQuantity = useMemo(() => {
    if (!component || component.cost <= 0) return 999999;
    return Math.floor(cash / component.cost);
  }, [cash, component]);

  const handleQuantityChange = useCallback((value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0) {
      setQuantity(num);
    } else if (value === '') {
      setQuantity(0);
    }
  }, []);

  const handleShortcut = useCallback((value: number) => {
    setQuantity(value);
  }, []);

  const handlePlaceOrder = useCallback(() => {
    if (!component) return;

    // Only validate affordability if placing an actual order
    if (quantity > 0 && !canOrder) return;

    // Convert string dropdown value to number | null
    const thresholdOption = AUTO_ORDER_THRESHOLDS.find(opt => opt.value === autoOrderThreshold);
    const thresholdValue = thresholdOption?.threshold ?? null;

    const autoOrderSettings: Omit<AutoOrderSetting, 'componentId'> = {
      // Preserve existing quantity for auto-orders if not placing an order
      quantity: quantity || existingAutoOrderSetting?.quantity || GAME_CONSTANTS.inventory.components.defaultAutoOrderQuantity,
      autoOrderThreshold: thresholdValue,
      recurringOrder: {
        enabled: recurringEnabled,
        frequency,
      },
    };

    try {
      onOrder(component.id, quantity, autoOrderSettings);
      onClose();
    } catch (error) {
      console.error('Failed to place order:', error);
      // Modal stays open so user can retry
    }
  }, [component, quantity, autoOrderThreshold, recurringEnabled, frequency, canOrder, onOrder, onClose, existingAutoOrderSetting]);

  if (!component) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Order ${component.name}`}
      size="md"
    >
      <div className="space-y-4">
        {/* Total Cost Header */}
        <div className="text-center pb-2 border-b border-theme-border">
          <span className="text-sm text-theme-text-muted">Total Cost: </span>
          <span className={`text-lg font-semibold ${canAfford ? 'text-theme-text-primary' : 'text-theme-error'}`}>
            {formatCurrency(totalCost)}
          </span>
          {!canAfford && totalCost > 0 && (
            <p className="text-xs text-theme-error mt-1">Insufficient funds</p>
          )}
        </div>

        {/* Quantity Input */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-theme-text-secondary mb-1">
            Quantity
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              id="quantity"
              min="0"
              max={maxQuantity}
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              className="flex-1 px-3 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-accent focus:border-theme-accent text-theme-text-primary bg-theme-bg-secondary"
              placeholder="Enter quantity"
            />
            <span className="text-sm text-theme-text-muted">units</span>
          </div>
        </div>

        {/* Quick Select Shortcuts */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
            Quick Select
          </label>
          <div className="flex gap-2">
            {GAME_CONSTANTS.inventory.components.orderShortcuts.map((shortcut) => (
              <button
                key={shortcut.value}
                onClick={() => handleShortcut(shortcut.value)}
                className="px-3 py-1.5 text-sm font-medium text-theme-accent bg-theme-accent/10 border border-theme-accent rounded-lg hover:bg-theme-accent/20 transition-colors"
              >
                {shortcut.label}
              </button>
            ))}
          </div>
        </div>

        {/* Auto-Order Settings */}
        <div className="pt-4 border-t border-theme-border space-y-3">
          {/* Auto-Order Threshold Dropdown */}
          <div className="flex items-center gap-2">
            <label htmlFor="auto-order-threshold" className="text-sm text-theme-text-secondary">
              Auto-order when inventory is at or below:
            </label>
            <select
              id="auto-order-threshold"
              value={autoOrderThreshold}
              onChange={(e) => setAutoOrderThreshold(e.target.value)}
              className="px-2 py-1 text-sm border border-theme-border rounded focus:ring-2 focus:ring-theme-accent focus:border-theme-accent text-theme-text-primary bg-theme-bg-secondary"
              aria-label="Auto order threshold"
            >
              {AUTO_ORDER_THRESHOLDS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Recurring Order */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={recurringEnabled}
                onChange={(e) => setRecurringEnabled(e.target.checked)}
                className="w-4 h-4 text-theme-accent border-theme-border rounded focus:ring-theme-accent"
                aria-label="Order this amount every"
              />
              <span className="text-sm text-theme-text-secondary">
                Order this amount every
              </span>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as Frequency)}
                disabled={!recurringEnabled}
                className="px-2 py-1 text-sm border border-theme-border rounded focus:ring-2 focus:ring-theme-accent focus:border-theme-accent text-theme-text-primary bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
            </label>

            {/* Warning */}
            {recurringEnabled && (
              <p className="text-xs text-theme-warning pl-6">
                Warning: Recurring orders may cause unexpected financial strain.
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-theme-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-theme-text-secondary bg-theme-bg-secondary border border-theme-border rounded-lg hover:bg-theme-bg-tertiary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handlePlaceOrder}
            disabled={quantity > 0 && !canOrder}
            className="px-4 py-2 text-sm font-medium text-white bg-theme-accent rounded-lg hover:bg-theme-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {quantity > 0 ? 'Place Order' : 'Save Settings'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
